/**
 * rother3 companion — the landing page.
 *
 * Four cards, in the order a reader meets them: put data in, add one by link, see what is here,
 * manage what is here.
 *
 * ## What changed and why
 *
 * - **Multi-file and ZIP import.** An owner comparing a hundred businesses has a hundred export
 *   files, and importing them one at a time is a chore that reads as the tool being broken. A
 *   ZIP is the one container a person can produce by selecting a folder and pressing "Compress".
 * - **The overview card.** "What have I got?" is the first question on returning to the page and
 *   the old page answered it with a list capped at ten, which looks like ten is all there is.
 * - **The business grid, paged and per-cell actions.** A hundred businesses in one column is a
 *   page nobody scrolls; notes moved here because a note belongs next to its business.
 *
 * ## The import is all-or-nothing, and says so
 *
 * `storeAudits` writes everything in one transaction. If any record fails, none are written.
 * A partial import is worse than a failed one: every record that landed renders and compares,
 * so a comparison over 90 businesses looks exactly like one over 137 while being wrong about
 * what it covers.
 */
import { parseMapsUrl } from './import.js';
import { getAllAudits, storeAudits, deleteAudit, deleteAllAudits, dedupeByPlace, anchor as anchorPref }
  from './storage.js';
import { readZipJsonFiles } from './zip.js';
import { buildZipExport, summariseRepeats } from './export-zip.js';
import { mountThemeToggle } from './theme.js';
import { initI18n, t } from './i18n.js';
import { escapeHtml, escapeAttribute } from './html.js';
import { auditFields, categoryNames } from './audit-shape.js';
import { count, ratingText, isoDate, timeAgo } from './format.js';

const dropZone = document.getElementById('drop-zone');
const fileInput = document.getElementById('file-input');
const shareLinkInput = document.getElementById('share-link');
const openLinkBtn = document.getElementById('open-link-btn');
const statusEl = document.getElementById('status');
const mapsUrlInput = document.getElementById('maps-url-input');
const parseMapsBtn = document.getElementById('parse-maps-btn');
const mapsParseResult = document.getElementById('maps-parse-result');
const overviewCard = document.getElementById('overview-card');
const overviewGrid = document.getElementById('overview-grid');
const businessesCard = document.getElementById('businesses-card');
const bizGrid = document.getElementById('biz-grid');
const bizPager = document.getElementById('biz-pager');
const bizFilter = document.getElementById('biz-filter');
const bizCountNote = document.getElementById('biz-count-note');
const bizRepeatNote = document.getElementById('biz-repeat-note');
const exportZipBtn = document.getElementById('export-zip-btn');
const importProgress = document.getElementById('import-progress');
const importProgressText = document.getElementById('import-progress-text');
const importProgressFill = document.getElementById('import-progress-fill');

/** Businesses per page in the grid. */
const BIZ_PAGE_SIZE = 24;

let bizFilterText = '';
let bizPage = 1;

/** The imported audits, newest first. Kept so filtering does not re-query storage per keystroke. */
let allAudits = [];

/**
 * Import timestamp per business, keyed on `placeId`.
 *
 * `dedupeByPlace` collapses repeat imports and returns only the fields, so the timestamp that
 * makes a grid sortable by recency is gone by the time the cells are built. Rebuilt alongside
 * it from the same records, keeping the **newest** import for each place — the same rule
 * `dedupeByPlace` applies, so the grid's dates and its rows always describe the same capture.
 *
 * A business with no readable Place ID has no key, and is not in the grid's address space at
 * all: it cannot be opened, noted or removed from here.
 */
const bizImportedAt = new Map();
function rebuildImportedAt() {
  bizImportedAt.clear();
  for (const audit of allAudits) {
    const placeId = auditFields(audit).placeId;
    if (typeof placeId !== 'string' || placeId === '') {continue;}
    if (!bizImportedAt.has(placeId)) {
      bizImportedAt.set(placeId, typeof audit.importedAt === 'string' ? audit.importedAt : '');
    }
  }
}

function showStatus(text, type = 'info') {
  statusEl.textContent = text;
  statusEl.className = `status status-${type}`;
  statusEl.hidden = false;
}

function setProgress(text, fraction) {
  if (text === null) {
    importProgress.hidden = true;
    return;
  }
  importProgress.hidden = false;
  importProgressText.textContent = text;
  importProgressFill.style.width = `${Math.round(Math.max(0, Math.min(1, fraction)) * 100)}%`;
}

/**
 * Parse one file's text into a storable audit, or return an error.
 *
 * Returns `{ ok: true, audit }` or `{ ok: false, reason }` rather than throwing, because one
 * malformed file in a 137-file import must be **named and skipped**, not abort the other 136.
 * The caller reports the count of skips and the first few names.
 */
function parseAuditJson(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'not valid JSON' };
  }
  if (!data || typeof data !== 'object') {return { ok: false, reason: 'not an audit object' };}

  // The duck-type the original import used, unchanged: an audit is anything with a schema
  // version and a type. Being stricter would reject share links and older v1 files.
  if (!data.schemaVersion && !data.type && !data.audit && !data.business) {
    return { ok: false, reason: 'no schemaVersion or type field' };
  }
  return { ok: true, audit: data };
}

/**
 * Turn a `FileList` into storable audits.
 *
 * A `.zip` contributes many audits; a `.json` contributes one. Each is parsed independently so a
 * single bad file is reported by name and the rest still import.
 */
async function collectAuditsFromFiles(files) {
  const audits = [];
  const failures = [];
  let done = 0;

  for (const file of files) {
    const name = file.name;
    const isZip = /\.zip$/i.test(name);

    if (isZip) {
      setProgress(`Reading ${name}…`, done / files.length);
      try {
        const buffer = await file.arrayBuffer();
        const members = await readZipJsonFiles(buffer);
        for (const member of members) {
          const parsed = parseAuditJson(member.text);
          if (parsed.ok) {audits.push(parsed.audit);} else {failures.push(`${member.name} (${parsed.reason})`);}
        }
      } catch (err) {
        // A ZIP that cannot be read is reported whole. Its contents are not guessed at.
        failures.push(`${name} (${err.message})`);
      }
    } else if (/\.json$/i.test(name)) {
      try {
        const parsed = parseAuditJson(await file.text());
        if (parsed.ok) {audits.push(parsed.audit);} else {failures.push(`${name} (${parsed.reason})`);}
      } catch (err) {
        failures.push(`${name} (${err.message})`);
      }
    } else {
      // Named, because "nothing happened" is the worst possible outcome of a file picker.
      failures.push(`${name} (not a .json or .zip file)`);
    }

    done += 1;
    setProgress(`Reading ${done} of ${files.length}…`, done / files.length);
  }

  return { audits, failures };
}

/**
 * Report what an import did, in numbers a reader can check.
 *
 * The skipped count is not optional. "Imported 137" with three files silently dropped is a
 * claim about coverage that the page then cannot support.
 */
function reportImport(imported, failures) {
  const parts = [];
  if (imported === 1) {parts.push('Imported 1 audit.');} else if (imported > 1) {parts.push(`Imported ${imported} audits.`);}

  if (failures.length === 0) {
    showStatus(parts.join(' ') || 'Nothing to import.', 'success');
    return;
  }

  const named = failures.slice(0, 3).join('; ');
  const more = failures.length > 3 ? ` and ${failures.length - 3} more` : '';
  showStatus(
    `${parts.join(' ') || 'Nothing imported.'} ${failures.length} file(s) could not be read: ${named}${more}.`,
    'error'
  );
}

async function handleFiles(fileList) {
  const files = Array.from(fileList ?? []);
  if (files.length === 0) {return;}

  setProgress(`Reading 0 of ${files.length}…`, 0);
  const { audits, failures } = await collectAuditsFromFiles(files);

  if (audits.length === 0) {
    setProgress(null, 0);
    reportImport(0, failures);
    return;
  }

  try {
    await storeAudits(audits);
  } catch (err) {
    setProgress(null, 0);
    showStatus(
      `Could not save ${audits.length} audit(s) to this browser's database. Nothing was imported. (${err.message})`,
      'error'
    );
    return;
  }

  setProgress(null, 0);
  reportImport(audits.length, failures);
  await loadBusinesses();
}

/**
 * The overview card: what is stored, in countable facts.
 *
 * Every figure is computed from the records rather than incremented on import, so a figure
 * cannot drift from the data it describes the way a stored counter can.
 */
function renderOverview() {
  const { fields } = dedupeByPlace(allAudits);
  const businesses = fields.length;
  const anchorPlaceId = anchorPref.get();
  const withReviews = fields.filter((f) => (Array.isArray(f.reviewsMeta?.items) ? f.reviewsMeta.items.length : 0) > 0).length;
  const withHours = fields.filter((f) => Array.isArray(f.hoursMeta?.parsed) && f.hoursMeta.parsed.length > 0).length;
  const withCategories = fields.filter((f) => Array.isArray(f.categories) && f.categories.length > 0).length;
  const withAbout = fields.filter((f) => typeof (f.description ?? f.about?.description) === 'string').length;
  const withPopularTimes = fields.filter((f) => f.popularTimes && typeof f.popularTimes === 'object').length;
  const withPhotos = fields.filter((f) => f.photos && typeof f.photos === 'object').length;
  const duplicates = allAudits.length - businesses;

  const stats = [
    [businesses, businesses === 1 ? 'business' : 'businesses'],
    [count(allAudits.reduce((n, f) => n + (f.reviewCount ?? 0), 0)), 'reviews on those listings'],
    [withReviews, withReviews === 1 ? 'has reviews read' : 'have reviews read'],
    [withCategories, withCategories === 1 ? 'has categories' : 'have categories'],
    [withHours, withHours === 1 ? 'has opening hours' : 'have opening hours'],
    [withAbout, withAbout === 1 ? 'has a description' : 'have a description'],
    [withPopularTimes, withPopularTimes === 1 ? 'has popular times' : 'have popular times'],
    [withPhotos, withPhotos === 1 ? 'has photos' : 'have photos'],
  ];

  overviewGrid.innerHTML = stats
    .map(([value, label]) => `
      <div class="overview-stat">
        <span class="overview-stat-value">${escapeHtml(String(value))}</span>
        <span class="overview-stat-label">${escapeHtml(label)}</span>
      </div>`)
    .join('');

  overviewCard.hidden = false;
  businessesCard.hidden = false;

  if (anchorPlaceId) {
    const mine = fields.find((f) => f.placeId === anchorPlaceId);
    if (mine) {
      const anchorNote = document.createElement('p');
      anchorNote.className = 'bubble-card-note';
      anchorNote.textContent = `Your business: ${mine.businessName ?? 'Unnamed business'}`;
      overviewCard.querySelector('.bubble-card-note')?.after(anchorNote);
    }
  }

  if (duplicates > 0) {
    const dupNote = document.createElement('p');
    dupNote.className = 'bubble-card-note';
    dupNote.textContent =
      `${duplicates} older ${duplicates === 1 ? 'import' : 'imports'} of the same ` +
      `${duplicates === 1 ? 'business are' : 'businesses are'} folded into the newest. ` +
      'Only the newest of each is compared.';
    overviewCard.querySelector('.bubble-card-note')?.after(dupNote);
  }
}

/**
 * State the repeats, at the top of the card.
 *
 * ## Why this is not optional
 *
 * The grid shows one card per **business**. The store holds one record per **import**. When those
 * differ, the reader has files they cannot see and no explanation, and a grid that silently
 * dropped rows is the same defect as a comparison that silently inflated a denominator — a
 * number on screen that nobody computed.
 *
 * So it says both numbers, and names the businesses that have an older version behind them.
 *
 * ## Why it is computed on every render
 *
 * A flag written at import time goes stale the moment a business is removed. `summariseRepeats`
 * reads the records themselves, so the count cannot disagree with what is stored.
 *
 * @param {{total: number, businesses: number, repeated: object[], unidentifiable: number}} s
 */
function renderRepeatNote(s) {
  if (bizRepeatNote === null) {return;}

  const extra = s.total - s.businesses;
  if (extra <= 0 && s.unidentifiable === 0) {
    // Nothing to explain. Saying "no repeats" every time would be noise on the common case.
    bizRepeatNote.hidden = true;
    bizRepeatNote.textContent = '';
    return;
  }

  const parts = [];
  if (extra > 0) {
    // `{count}` is substituted here, not by `t()`, which does no interpolation.
    //
    // Getting this wrong is not cosmetic, and both halves of it shipped broken first. Passing
    // the whole sentence as `t()`'s fallback argument loses it — `t()` returns the catalogue
    // string, so a sentence that carried its own data in that fallback silently discarded the
    // data, and the note read "Newest kept:" with nothing after it and "{count}" where a number
    // belonged.
    parts.push(
      t('biz_repeat_summary', `${extra} older ${extra === 1 ? 'version' : 'versions'} of the same business are also stored.`)
        .replace('{count}', String(extra)),
    );
  }
  if (s.unidentifiable > 0) {
    // A record with no readable Place ID cannot be matched against anything, so it can never be
    // collapsed and can never be recognised as a repeat. Stating it is the only honest option.
    parts.push(t('biz_repeat_unidentifiable', 'Some records have no Place ID, so rother3 cannot tell whether they are repeats.'));
  }
  if (s.repeated.length > 0) {
    const names = s.repeated
      .slice(0, 4)
      .map((r) => `${r.name || t('biz_repeat_unnamed', 'an unnamed business')} ×${r.copies}`)
      .join(', ');
    const more = s.repeated.length > 4
      ? t('biz_repeat_more', `and ${s.repeated.length - 4} more`).replace('{count}', String(s.repeated.length - 4))
      : '';
    // "Newest kept" is stated because it is the rule the reader cannot otherwise infer: the
    // card they see is the freshest read, and the older ones are still in the file.
    //
    // The names are appended **outside** `t()` for the reason above.
    parts.push(`${t('biz_repeat_which', 'Newest kept:')} ${names}${more ? `, ${more}` : ''}`);
  }

  bizRepeatNote.hidden = false;
  bizRepeatNote.textContent = parts.join(' · ');
}

/**
 * One cell per business, with remove and notes.
 *
 * The remove control is a `<button>` inside a cell that is **not** itself a link or a button,
 * because nesting interactive controls is invalid HTML and a keyboard user tabbing through the
 * grid would land on the row action and the cell action in an undefined order. The cell's name
 * is a separate button that opens the business.
 */
function renderBizGrid() {
  rebuildImportedAt();
  const { fields } = dedupeByPlace(allAudits);
  const anchorPlaceId = anchorPref.get();
  const repeats = summariseRepeats(allAudits);

  renderRepeatNote(repeats);

  const needle = bizFilterText.trim().toLowerCase();
  const filtered = needle === ''
    ? fields
    : fields.filter((f) => {
      const haystack = [f.businessName, f.placeId, f.address, f.phone, categoryNames(f.categories)]
        .filter((s) => typeof s === 'string')
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / BIZ_PAGE_SIZE));
  bizPage = Math.min(Math.max(1, bizPage), pageCount);
  const start = (bizPage - 1) * BIZ_PAGE_SIZE;
  const visible = filtered.slice(start, start + BIZ_PAGE_SIZE);

  if (total === 0) {
    bizGrid.innerHTML = `<li class="fam-empty">${escapeHtml(
      needle === '' ? 'No businesses imported yet.' : `No business matches “${bizFilterText}”.`
    )}</li>`;
    bizPager.innerHTML = '';
    bizCountNote.textContent = '';
    // A repeat count survives a filter that hides everything; leaving it would say "3 imports"
    // above an empty grid. The card says nothing when there is nothing to say.
    if (bizRepeatNote !== null && needle !== '') {
      bizRepeatNote.hidden = true;
      bizRepeatNote.textContent = '';
    }
    return;
  }

  bizGrid.innerHTML = visible
    .map((f, i) => {
      const name = typeof f.businessName === 'string' && f.businessName.trim() !== ''
        ? f.businessName.trim()
        : `Business ${start + i + 1}`;
      const isAnchor = f.placeId === anchorPlaceId;
      const openLabel = isAnchor ? 'Open your business' : 'Open';
      const copies = repeats.byPlaceId.get(f.placeId) ?? 1;
      const rating = parseFloat(f.rating);
      const figures = [
        Number.isFinite(rating) ? ratingText(rating) : null,
        Number.isFinite(parseFloat(f.reviewCount)) ? `${count(f.reviewCount)} reviews` : null,
      ]
        .filter(Boolean)
        .join(' · ') || 'No rating or review count';

      // Every button names its business in its accessible name. A grid of 24 cells each with a
      // bare "Remove" is unusable with a screen reader: the reader announces "Remove, button"
      // twenty-four times and the user has to guess which one they are on. The visible label
      // stays short; the accessible name carries the business.
      /**
         * Both dates, because each answers a different question.
         *
         * `importedAt` is when *this browser* received the audit; `extractedAt` is when rother3
         * read the listing. They are usually minutes apart and occasionally months apart — a
         * file exported in March and imported today. A grid sorted by recency is asking about
         * the import, so the import date leads and the extraction date is beneath it.
         */
        const importedAt = bizImportedAt.get(f.placeId) ?? '';
              return `<li class="biz-cell${isAnchor ? ' biz-cell--anchor' : ''}">
        <button type="button" class="biz-cell-name" data-open-place="${escapeAttribute(f.placeId ?? '')}"
                aria-label="${escapeAttribute(`${openLabel} ${name}`)}">
          ${isAnchor ? '<span aria-hidden="true">◆</span> ' : ''}${escapeHtml(name)}
        </button>
        <span class="biz-cell-figures">${escapeHtml(figures)}</span>
        <span class="biz-cell-when">
          <span class="biz-cell-ago">Imported ${escapeHtml(timeAgo(importedAt))}</span>
          <span class="biz-cell-date">${escapeHtml(isoDate(importedAt))}</span>
        </span>
        ${copies > 1
          // The card is the newest read; the badge says how many are behind it, so "why do I
          // have 13 files and 11 businesses" is answerable on the card that caused it.
          ? `<span class="biz-cell-repeat">${escapeHtml(
            t('biz_cell_repeat', `${copies} imports · newest kept`).replace('{count}', String(copies)))}</span>`
          : ''}
        <span class="biz-cell-actions">
          <button type="button" class="biz-cell-btn" data-notes-place="${escapeAttribute(f.placeId ?? '')}"
                  aria-label="${escapeAttribute(`Notes for ${name}`)}">Notes</button>
          <button type="button" class="biz-cell-btn biz-cell-btn--remove" data-remove-place="${escapeAttribute(f.placeId ?? '')}"
                  aria-label="${escapeAttribute(`Remove ${name}`)}">Remove</button>
        </span>
      </li>`;
    })
    .join('');

  const range =
    pageCount === 1
      ? `Showing all ${total} ${total === 1 ? 'business' : 'businesses'}`
      : `Showing ${start + 1}–${Math.min(start + BIZ_PAGE_SIZE, total)} of ${total}`;
  bizCountNote.textContent = range;

  bizPager.innerHTML =
    pageCount <= 1
      ? ''
      : `<div class="fam-pager">
          <p class="fam-count">${escapeHtml(range)}</p>
          <span class="fam-pager-buttons">
            <button type="button" class="btn btn-secondary fam-page" data-biz-page="${bizPage - 1}"${bizPage <= 1 ? ' disabled' : ''}>Previous</button>
            <span class="fam-pager-state">Page ${bizPage} of ${pageCount}</span>
            <button type="button" class="btn btn-secondary fam-page" data-biz-page="${bizPage + 1}"${bizPage >= pageCount ? ' disabled' : ''}>Next</button>
          </span>
        </div>`;
}

/**
 * Notes, keyed on `placeId`, in `localStorage`.
 *
 * The same key the workspace's notes section has always used, so notes written before this
 * change are still there. A business with no readable Place ID cannot carry notes, and says so
 * rather than silently dropping what the reader typed.
 */
const notes = {
  get(placeId) {
    if (!placeId) {return '';}
    try {return localStorage.getItem(`rother3-notes-${placeId}`) ?? '';} catch {return '';}
  },
  set(placeId, text) {
    if (!placeId) {return false;}
    try {localStorage.setItem(`rother3-notes-${placeId}`, text); return true;} catch {return false;}
  },
  clear(placeId) {
    if (!placeId) {return;}
    try {localStorage.removeItem(`rother3-notes-${placeId}`);} catch { /* nothing to do */ }
  },
};

function openNotesDialog(placeId, name) {
  if (!placeId) {
    showStatus(t('notes_no_place_id', 'This audit has no Place ID, so it cannot carry notes.'), 'error');
    return;
  }
  const current = notes.get(placeId);
  const next = window.prompt(t('notes_prompt', 'Notes for this business:'), current);
  if (next === null) {return;}
  notes.set(placeId, next);
  showStatus(
    next.trim() === '' ? t('notes_cleared', 'Notes cleared.') : t('notes_saved', 'Notes saved.'),
    'success'
  );
}

async function removeBusiness(placeId, name) {
  if (!placeId) {
    showStatus(t('remove_no_place_id', 'This audit has no Place ID, so it cannot be removed here.'), 'error');
    return;
  }
  const ok = window.confirm(
    t('remove_confirm', 'Remove this business?').replace('{name}', name) +
    '\n\nThis deletes every copy stored in this browser.'
  );
  if (!ok) {return;}

  // Delete every record for this place, not one. A re-import leaves several, and deleting one
  // would leave the business still on the page — which reads as a failed delete.
  const ids = allAudits.filter((a) => auditFields(a).placeId === placeId).map((a) => a.id);
  try {
    for (const id of ids) {await deleteAudit(id);}
    notes.clear(placeId);
    if (anchorPref.get() === placeId) {anchorPref.set(null);}
    showStatus(t('remove_done', 'Removed.'), 'success');
    await loadBusinesses();
  } catch (err) {
    showStatus(t('remove_failed', 'Could not remove this business.'), 'error');
  }
}

async function loadBusinesses() {
  try {
    allAudits = await getAllAudits();
  } catch {
    allAudits = [];
  }

  if (allAudits.length === 0) {
    overviewCard.hidden = true;
    businessesCard.hidden = true;
    return;
  }

  renderOverview();
  renderBizGrid();
}

// ── Events ─────────────────────────────────────────────────────────────────

dropZone?.addEventListener('click', (e) => {
  // The label inside the drop zone is a real `<label for>`, so a click on it must not also
  // trigger the zone's own file-dialog click or the picker opens twice.
  if (e.target.closest('label')) {return;}
  fileInput.click();
});

dropZone?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fileInput.click();
  }
});

dropZone?.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropZone.classList.add('dragover');
});

dropZone?.addEventListener('dragleave', () => {
  dropZone.classList.remove('dragover');
});

dropZone?.addEventListener('drop', (e) => {
  e.preventDefault();
  dropZone.classList.remove('dragover');
  handleFiles(e.dataTransfer?.files);
});

fileInput?.addEventListener('change', () => {
  handleFiles(fileInput.files);
  // Reset so re-picking the same file fires `change` again. Without this, importing the same
  // file twice in a row silently does nothing the second time.
  fileInput.value = '';
});

bizGrid?.addEventListener('click', (e) => {
  const open = e.target.closest('[data-open-place]');
  if (open) {
    window.location.href = `workspace.html?place=${encodeURIComponent(open.dataset.openPlace)}`;
    return;
  }
  const notesBtn = e.target.closest('[data-notes-place]');
  if (notesBtn) {
    const placeId = notesBtn.dataset.notesPlace;
    const f = dedupeByPlace(allAudits).fields.find((x) => x.placeId === placeId);
    openNotesDialog(placeId, f?.businessName ?? t('biz_unnamed', 'this business'));
    return;
  }
  const removeBtn = e.target.closest('[data-remove-place]');
  if (removeBtn) {
    const placeId = removeBtn.dataset.removePlace;
    const f = dedupeByPlace(allAudits).fields.find((x) => x.placeId === placeId);
    removeBusiness(placeId, f?.businessName ?? t('biz_unnamed', 'this business'));
  }
});

bizPager?.addEventListener('click', (e) => {
  const button = e.target.closest('[data-biz-page]');
  if (!button || button.disabled) {return;}
  bizPage = parseInt(button.dataset.bizPage, 10);
  renderBizGrid();
});

bizFilter?.addEventListener('input', (e) => {
  bizFilterText = e.target.value;
  bizPage = 1;
  renderBizGrid();
});

/**
 * Write every stored audit back out as one re-importable ZIP.
 *
 * ## Why this sits beside the grid and not on the workspace
 *
 * This is the inverse of the importer, and the importer lives here. A reader who has 100 listings
 * in this browser and wants them on a phone should not have to open another page to get them out.
 *
 * ## Why it writes every record, not the deduplicated set
 *
 * The grid shows one card per business; the store holds one record per import. Exporting only the
 * visible cards would quietly drop the history a reader may want — and would make the file smaller
 * in a way that looks like tidiness.
 *
 * ## Why it cannot fail silently
 *
 * The build throws on an empty store rather than producing an archive that imports nothing, and
 * the catch reports the reason. A button that does nothing looks identical to a button that is
 * broken.
 */
exportZipBtn?.addEventListener('click', () => {
  try {
    const file = buildZipExport(allAudits);
    const url = URL.createObjectURL(file.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.filename;
    a.click();
    // Revoked on the next tick, not immediately: revoking in the same statement cancels the
    // download in some browsers, because the navigation has not started yet.
    setTimeout(() => URL.revokeObjectURL(url), 0);

    showStatus(
      file.businessCount === 1
        ? t('export_zip_done_one', 'Exported 1 business.')
        : t('export_zip_done', `Exported ${file.businessCount} audits, including ${summariseRepeats(allAudits).repeated.length ? 'older versions' : 'every business'}.`)
          .replace('{count}', String(file.businessCount)),
      'success',
    );
  } catch (err) {
    showStatus(`${t('export_zip_failed', 'Could not build the ZIP:')} ${err instanceof Error ? err.message : ''}`.trim(), 'error');
  }
});

document.getElementById('remove-all-btn')?.addEventListener('click', async () => {
  const ok = window.confirm(
    t('remove_all_confirm', 'Remove every business stored in this browser?') +
    '\n\nThis cannot be undone.'
  );
  if (!ok) {return;}
  try {
    await deleteAllAudits();
    anchorPref.set(null);
    showStatus(t('remove_all_done', 'Everything removed.'), 'success');
    await loadBusinesses();
  } catch {
    showStatus(t('remove_all_failed', 'Could not remove everything.'), 'error');
  }
});

// Share links ---------------------------------------------------------------

openLinkBtn?.addEventListener('click', () => {
  const url = shareLinkInput.value.trim();
  if (!url) {
    showStatus(t('share_empty', 'Paste a share link first.'), 'error');
    return;
  }
  try {
    const parsed = new URL(url);
    if (parsed.hash.startsWith('#d=')) {
      window.location.href = `workspace.html${parsed.hash}`;
      return;
    }
    showStatus(t('share_no_data', 'No data found in link. Share links must contain #d= data.'), 'error');
  } catch {
    showStatus(t('share_invalid', 'Invalid URL format.'), 'error');
  }
});

// Maps URL ------------------------------------------------------------------

parseMapsBtn?.addEventListener('click', () => {
  const url = mapsUrlInput.value.trim();
  if (!url) {
    showStatus(t('maps_empty', 'Paste a Google Maps business link first.'), 'error');
    return;
  }
  const parsed = parseMapsUrl(url);
  if (parsed === null) {
    showStatus(t('maps_unreadable', 'That link does not contain a business. Check it and try again.'), 'error');
    return;
  }

  const rows = [
    ['Business name', parsed.businessName ?? 'Not in the link'],
    ['Place ID', parsed.placeId ?? 'Not found'],
    ['Coordinates', parsed.coordinates ?? 'Not found'],
    ['CID', parsed.cid ?? 'Not found'],
    ['Original URL', parsed.originalUrl ?? 'Not found'],
  ];

  mapsParseResult.innerHTML = rows
    .map(([label, value]) => `
      <div class="detail-field">
        <span class="detail-field-label">${escapeHtml(label)}</span>
        <span class="detail-field-value">${escapeHtml(value)}</span>
      </div>`)
    .join('');

  mapsParseResult.hidden = false;

  const actions = document.createElement('div');
  actions.className = 'detail-actions';

  const openMaps = document.createElement('a');
  openMaps.className = 'btn btn-secondary';
  openMaps.href = parsed.originalUrl ?? url;
  openMaps.target = '_blank';
  openMaps.rel = 'noopener noreferrer';
  openMaps.textContent = 'Open in Google Maps →';
  actions.appendChild(openMaps);

  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'btn btn-secondary';
  copyBtn.textContent = 'Copy Place ID';
  copyBtn.addEventListener('click', async () => {
    if (!parsed.placeId) {return;}
    try {
      await navigator.clipboard.writeText(parsed.placeId);
      copyBtn.textContent = 'Copied';
      setTimeout(() => {copyBtn.textContent = 'Copy Place ID';}, 1500);
    } catch {
      showStatus(t('maps_copy_failed', 'Could not copy. Select the text and copy it manually.'), 'error');
    }
  });
  actions.appendChild(copyBtn);

  mapsParseResult.appendChild(actions);
  showStatus(t('maps_ok', 'Link read. To get the full listing, open it in Maps with the rother3 extension.'), 'success');
});

// Boot ----------------------------------------------------------------------

/**
 * `initI18n()` completes before anything renders.
 *
 * `initI18n()` fetches the catalogue and then applies it to every `[data-i18n]` element. If a
 * render happens while that fetch is in flight, the render's text is overwritten by the static
 * placeholder — the page shows "Loading…" over a grid it has already drawn. The grid reads
 * `t()` at build time for its own strings, so it is unaffected; only the static markup is at
 * risk, which is why this is an ordering fix rather than a re-architecture.
 *
 * A function rather than top-level `await`, because this file is loaded as a classic module
 * entry by Vite and the boot order is the whole point.
 */
async function boot() {
  mountThemeToggle();
  await initI18n();
  await loadBusinesses();
}

boot();